import React, { useState, useEffect, useCallback } from "react";
import { FlexDiv } from "../../../ui/FlexDiv";
import styled from "styled-components";
import { mobileSize } from "../../../../util/variables";
import theme from "../../../../util/theme";
import Check from "../../../../content/img/icon/CheckMark.svg";
import FiveStars from "../../../ui/FiveStars";
import { AppText } from "../../../ui";
import Reviews from "../../../../content/assets/award-5k-reviews.webp";

const LOADING_MESSAGES = ["Setting goals", "Adapting growth areas", "Choosing content"];
const BOTTOM_LOADING_MESSAGES = [
  {
    type: "quote",
    text: <>“This app has greatly improved my life and overall well-being... it has helped me to foster a healthy <b>growth mindset.</b>”</>,
  },
  {
    type: "quote",
    text: <>“Very effective and <b>thought-provoking.</b> A very clever way to practice <b>self-care.</b>”</>,
  },
  {
    type: "image",
    src: Reviews,
  },
];

const LoadingBars = () => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [bottomMessageIndex, setBottomMessageIndex] = useState(0);

  const handleAnimationComplete = useCallback(() => {
    setCurrentMessageIndex(prev => prev + 1);
  }, []);

  useEffect(() => {
    if (currentMessageIndex >= LOADING_MESSAGES.length) {
      setAnimationComplete(true);
      return;
    }
  }, [currentMessageIndex]);
  useEffect(() => {
    // Switch between bottom messages every 3 seconds until reaching the last one
    const interval = setInterval(() => {
      setBottomMessageIndex(prev => {
        if (prev >= BOTTOM_LOADING_MESSAGES.length - 1) {
          clearInterval(interval);
          return prev;
        }
        return prev + 1;
      });
    }, 4600);

    return () => clearInterval(interval);
  }, []);

  const currentBottomMessage = BOTTOM_LOADING_MESSAGES[bottomMessageIndex];

  return (
    <FlexDiv
      direction="column"
      align="center"
      justify="space-between"
      // gap={1}
      // mobileGap={100}
      style={{ position: "relative", overflow: "hidden", height: "100%", maxWidth: "100%", width: "556px" }}
    >
      <StyledH2>
        {animationComplete
          ? <>Your Growth Plan is <span style={{ color: theme.PRIMARY_COLOR }}>ready</span>!</>
          : "Generating your personalized Growth Plan…"
        }
      </StyledH2>
      <FlexDiv direction="column" justify="center" align="center" gap={16} style={{ width: "100%" }}>
        {LOADING_MESSAGES.map((message, index) => (
          <LoadingBar
            key={message}
            message={message}
            isActive={index === currentMessageIndex}
            isComplete={index < currentMessageIndex}
            onComplete={handleAnimationComplete}
          />
        ))}
      </FlexDiv>

      <BottomMessage>
        {currentBottomMessage.type === "quote" ? (
          <Box>
            <AppText noMargin fontSize={20} mobileFontSize={16} style={{ lineHeight: 1.25 }}>
              {currentBottomMessage.text}
            </AppText>
            <FiveStars color={theme.PRIMARY_COLOR} mobileWidth={16} />
          </Box>
        ) : (
          <ReviewsImage src={currentBottomMessage.src} alt="Reviews" />
        )}
      </BottomMessage>
    </FlexDiv>
  );
};

interface LoadingBarProps {
  message: string;
  isActive: boolean;
  isComplete: boolean;
  onComplete: () => void;
}

const LoadingBar = ({ message, isActive, isComplete, onComplete }: LoadingBarProps) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (isActive) {
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            // onComplete();
            return 100;
          }

          return prev + 1;
        });
      }, 30); // 3000ms / 100 steps = 30ms per step

      return () => clearInterval(interval);
    }
  }, [isActive, onComplete]);

  useEffect(() => {
    if (progress >= 100) {
      onComplete();
    }
  }, [progress, onComplete]);

  return (
    <FlexDiv direction="column" gap={8} style={{ padding: "0 16px", opacity: isActive || isComplete ? 1 : 0, width: "100%" }}>
      <FlexDiv align="center" justify="space-between">
        <CompleteMessage style={{ fontWeight: isComplete ? 500 : 400 }}>{message}</CompleteMessage>
        <CheckContainer
          style={{ opacity: isComplete ? 1 : 0, rotate: isComplete ? "0deg" : "720deg" }}
        >
          <img src={Check} alt="Check" width={24} height={24} style={{ filter: "invert(1)" }} />
        </CheckContainer>
      </FlexDiv>
      <BarContainer style={{ height: isComplete ? "1px" : "10px" }}>
        <Bar style={{ width: `${isComplete ? 100 : progress}%`, background: isComplete ? "inherit" : theme.PRIMARY_COLOR }} />
      </BarContainer>
    </FlexDiv>
  );
};

const StyledH2 = styled.h2`
  max-width: 24ch;
  margin: 0 auto;
  text-align: center;
  font-size: 28px;
  line-height: 32px;
  font-weight: 500;

  @media ${mobileSize} {
    font-size: 20px;
    line-height: 28px;
  }
`;

const CompleteMessage = styled.span`
  font-size: 20px;
  line-height: 32px;

  @media ${mobileSize} {
    font-size: 16px;
    line-height: 24px;
  }
`;

const CheckContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 2px solid ${theme.WHITE_COLOR};
  border-radius: 50%;
  background-color: ${theme.PRIMARY_COLOR};
  transition: all 0.6s ease-out;
`;

const BarContainer = styled.div`
  background-color: #e5e7eb;
  border-radius: 9999px;
  height: 10px;
  width: 100%;
  transition: all 0.4s ease-out;
`;

const Bar = styled.div`
  background-color: ${theme.PRIMARY_COLOR};
  border-radius: 9999px;
  height: 100%;
  transition: all 0.4s ease-out;
`;

const BottomMessage = styled.div`
  margin-bottom: 32px;

  @media ${mobileSize} {
    margin-bottom: 0px;
  }
`;

const Box = styled.div`
  max-width: 556px;
  width: 100%;
  display: flex;
  flex-direction: column;

  padding: 24px;
  border-radius: 16px;
  background-color: #EDF1DD;

  p:first-child {
    margin-bottom: 16px;
  }
  
  @media ${mobileSize} {
    padding: 16px;
    
    p:first-child {
      margin-bottom: 12px;
    }
  }
`;

const ReviewsImage = styled.img`
  width: 253px;
  height: 90px;
  margin-bottom: 32px;

  @media ${mobileSize} {
    width: 202px;
    height: 72px;
  }
`;

export default LoadingBars;
